
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Volume2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Capacitor } from '@capacitor/core';
import { TextToSpeech } from '@capacitor-community/text-to-speech';

interface TextToSpeechButtonProps {
  text: string;
}

const TextToSpeechButton: React.FC<TextToSpeechButtonProps> = ({ text }) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const { toast } = useToast();
  const stopRequestedRef = React.useRef(false);
  const sessionRef = React.useRef(0);

  const stopSpeechPlayback = async () => {
    if (Capacitor.isNativePlatform()) {
      await TextToSpeech.stop();
      return;
    }

    window.speechSynthesis.cancel();
  };

  // Stop speech on unmount (e.g. navigating away)
  React.useEffect(() => {
    return () => {
      stopRequestedRef.current = true;
      sessionRef.current += 1;

      if (Capacitor.isNativePlatform()) {
        TextToSpeech.stop().catch(() => {});
      } else {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const speak = async () => {
    const trimmedText = text.trim();

    if (!trimmedText) {
      toast({
        title: "No content",
        description: "There is no text to read.",
        variant: "destructive",
      });
      return;
    }

    const currentSession = sessionRef.current + 1;
    sessionRef.current = currentSession;
    stopRequestedRef.current = false;

    try {
      setIsSpeaking(true);

      if (Capacitor.isNativePlatform()) {
        // Split text into smaller chunks to prevent crashes on iOS
        const chunks = (trimmedText.match(/.{1,1000}(\s|$)/g) || [])
          .map((chunk) => chunk.trim())
          .filter(Boolean);

        // Ensure any prior utterance is stopped before starting a new one
        try { await TextToSpeech.stop(); } catch {}

        for (const chunk of chunks) {
          if (stopRequestedRef.current || sessionRef.current !== currentSession) break;

          // NOTE: do not pass `category` — it is not a valid option in
          // @capacitor-community/text-to-speech v5 and causes the native
          // call to reject silently on iOS (no audio plays).
          await TextToSpeech.speak({
            text: chunk,
            lang: 'en-US',
            rate: 1.0,
            pitch: 1.0,
            volume: 1.0,
          });

          if (stopRequestedRef.current || sessionRef.current !== currentSession) {
            break;
          }
        }

        if (sessionRef.current === currentSession && !stopRequestedRef.current) {
          setIsSpeaking(false);
        }
      } else {
        // Web fallback using browser's Speech Synthesis
        const utterance = new SpeechSynthesisUtterance(trimmedText);

        utterance.onend = () => {
          if (sessionRef.current === currentSession && !stopRequestedRef.current) {
            setIsSpeaking(false);
          }
        };

        utterance.onerror = (event) => {
          console.error('Speech synthesis error:', event);
          if (sessionRef.current === currentSession) {
            setIsSpeaking(false);
          }
          toast({
            title: "Error",
            description: "Failed to read the text. Please try again.",
            variant: "destructive",
          });
        };

        window.speechSynthesis.cancel(); // Cancel any ongoing speech

        if (stopRequestedRef.current || sessionRef.current !== currentSession) {
          setIsSpeaking(false);
          return;
        }

        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      if (stopRequestedRef.current || sessionRef.current !== currentSession) {
        return;
      }

      console.error('Text-to-speech error:', error);
      setIsSpeaking(false);
      toast({
        title: "Error",
        description: "Text-to-speech failed. Please try again.",
        variant: "destructive",
      });
    }
  };

  const stopSpeaking = async () => {
    stopRequestedRef.current = true;
    sessionRef.current += 1;
    setIsSpeaking(false);

    try {
      await stopSpeechPlayback();
    } catch (error) {
      console.error('Error stopping speech:', error);
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={isSpeaking ? stopSpeaking : speak}
      title={isSpeaking ? "Stop reading" : "Read content aloud"}
    >
      <Volume2 className={`h-4 w-4 ${isSpeaking ? 'text-blue-500' : 'text-foreground'}`} />
    </Button>
  );
};

export default TextToSpeechButton;
